/**
 * EspaceOuvrier — Vue dédiée à l'ouvrier (tablette/mobile) et au chef d'atelier.
 *
 * Règles d'affichage des boutons START / PAUSE / RESUME / END :
 *   - Ouvrier connecté  : boutons actifs uniquement sur ses propres tâches
 *   - Chef / Admin / Atelier : boutons actifs sur toutes les tâches
 *
 * Dans les deux cas, l'action est envoyée au backend qui enregistre l'auteur
 * réel (effectue_par_nom) dans journal_taches — piste d'audit complète.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Play, Pause, RotateCcw, Square, Clock, User, Package, RefreshCw } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../services/api';

const ROLES_SUPERVISEUR = ['chef_atelier', 'atelier', 'admin'];

function EspaceOuvrier({ ouvrierIdCible = null, user: userProp = null }) {
  const { user: authUser } = useAuth();
  const user = userProp || authUser;
  // For ouvrier role, use their linked ouvrier_id; otherwise use the explicit param
  const resolvedOuvrierId = ouvrierIdCible || (user?.role === 'ouvrier' ? user?.ouvrier_id : null);
  const [taches, setTaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null); // affectation_id en cours d'action
  const [showTerminerModal, setShowTerminerModal] = useState(false);
  const [tacheATerminer, setTacheATerminer] = useState(null);
  const [formTerminer, setFormTerminer] = useState({ poids_bijou_fini: '', or_restant: '', notes: '' });

  const estSuperviseur = user && ROLES_SUPERVISEUR.includes(user.role);

  // Charge les tâches : ouvrier → ses propres tâches ; chef/admin → tâches de ouvrierIdCible
  const loadTaches = useCallback(async () => {
    try {
      setError(null);
      const params = resolvedOuvrierId ? `?ouvrier_id=${resolvedOuvrierId}` : '';
      const res = await fetch(`${API_URL}/espace-ouvrier/mes-taches${params}`);
      if (!res.ok) {
        const d = await res.json();
        setError(d.error || 'Erreur chargement');
        return;
      }
      const data = await res.json();
      setTaches(data);
    } catch (e) {
      setError('Erreur réseau');
    } finally {
      setLoading(false);
    }
  }, [resolvedOuvrierId]);

  useEffect(() => {
    loadTaches();
    const interval = setInterval(loadTaches, 15000);
    return () => clearInterval(interval);
  }, [loadTaches]);

  // Envoie une action au backend
  const envoyerAction = async (affectation_id, action, extra = {}) => {
    setActionLoading(affectation_id);
    try {
      const res = await fetch(`${API_URL}/espace-ouvrier/tache/${affectation_id}/action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, user_id: user?.id, user_nom: user?.nom_complet, ...extra })
      });
      const data = await res.json();
      if (!res.ok) {
        alert(`Erreur : ${data.error}`);
      } else {
        await loadTaches();
      }
    } catch (e) {
      alert('Erreur réseau');
    } finally {
      setActionLoading(null);
    }
  };

  const handlePause = (aff) => envoyerAction(aff.id, 'pause');
  const handleReprendre = (aff) => envoyerAction(aff.id, 'reprise');

  const handleTerminer = (aff) => {
    setTacheATerminer(aff);
    setFormTerminer({ poids_bijou_fini: '', or_restant: '', notes: '' });
    setShowTerminerModal(true);
  };

  const confirmerTerminer = async () => {
    if (!tacheATerminer) return;
    await envoyerAction(tacheATerminer.id, 'fin', {
      poids_bijou_fini: parseFloat(formTerminer.poids_bijou_fini) || 0,
      or_restant: parseFloat(formTerminer.or_restant) || 0,
      notes: formTerminer.notes
    });
    setShowTerminerModal(false);
    setTacheATerminer(null);
  };

  // Détermine si les boutons d'action sont accessibles pour cette tâche
  // Chef/admin/atelier : toujours autorisé
  // Ouvrier : seulement ses propres tâches (le backend vérifie aussi côté serveur)
  const peutAgir = () => {
    if (estSuperviseur) return true;
    // Pour un ouvrier, on affiche les boutons — le backend fera la vérification finale
    return true;
  };

  const statutLabel = (statut) => {
    if (statut === 'en_cours') return { label: 'En cours', color: 'text-green-600 bg-green-50' };
    if (statut === 'pause') return { label: 'En pause', color: 'text-yellow-600 bg-yellow-50' };
    return { label: statut, color: 'text-gray-500 bg-gray-50' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Clock className="animate-spin text-gray-400 mr-2" size={20} />
        <span className="text-gray-500">Chargement des tâches...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* En-tête contextuel */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <User size={18} className="text-gray-500" />
          <span className="font-medium text-gray-700">
            {estSuperviseur && resolvedOuvrierId
              ? `Tâches de l'ouvrier #${resolvedOuvrierId}`
              : user?.nom_complet
                ? `Mes tâches — ${user.nom_complet}`
                : 'Mes tâches en cours'}
          </span>
        </div>
        <button
          onClick={loadTaches}
          className="text-sm text-blue-600 hover:underline"
        >
          Actualiser
        </button>
      </div>

      {/* Note superviseur */}
      {estSuperviseur && (
        <div className="text-xs text-blue-600 bg-blue-50 border border-blue-200 rounded px-3 py-2">
          Mode superviseur — vous pouvez agir sur toutes les tâches.
          Vos actions sont tracées dans le journal (audit).
        </div>
      )}

      {taches.length === 0 && (
        <div className="text-center py-8 text-gray-400">
          <Package size={32} className="mx-auto mb-2 opacity-40" />
          Aucune tâche en cours
        </div>
      )}

      {/* Liste des tâches */}
      {taches.map((tache) => {
        const en_cours = tache.statut === 'en_cours';
        const en_pause = tache.statut === 'pause';
        const isLoading = actionLoading === tache.id;
        const statutInfo = statutLabel(tache.statut);
        const canAct = peutAgir(tache);

        return (
          <div
            key={tache.id}
            className="bg-white rounded-xl border border-gray-200 shadow-sm p-4"
          >
            {/* Titre tâche */}
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="font-semibold text-gray-800">
                  {tache.numero_ordre} — {tache.nom_etape || tache.type_bijou}
                </div>
                <div className="text-sm text-gray-500">{tache.ref_article || tache.mission}</div>
                {tache.of_description && (
                  <div className="text-xs text-gray-400 mt-1">{tache.of_description}</div>
                )}
              </div>
              <span className={`text-xs font-medium px-2 py-1 rounded-full ${statutInfo.color}`}>
                {statutInfo.label}
              </span>
            </div>

            {/* Ouvrier assigné (visible pour superviseur) */}
            {estSuperviseur && (
              <div className="text-xs text-gray-500 mb-2">
                Ouvrier assigné : {tache.ouvrier_prenom} {tache.ouvrier_nom}
              </div>
            )}

            {/* Méta */}
            <div className="text-xs text-gray-400 mb-3">
              {tache.duree_estimee_heures && <>Estimé : {tache.duree_estimee_heures}h</>}
              {tache.date_debut && (
                <> &nbsp;·&nbsp; Démarré : {new Date(tache.date_debut).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</>
              )}
              {tache.date_livraison_prevue && (
                <> &nbsp;·&nbsp; Livraison : {new Date(tache.date_livraison_prevue).toLocaleDateString('fr-FR')}</>
              )}
            </div>

            {/* Boutons d'action — accessibles à l'ouvrier ET au chef/admin */}
            {canAct && (
              <div className="flex gap-2 flex-wrap">
                {/* DÉMARRER — visible si en_attente */}
                {tache.statut === 'en_attente' && (
                  <button
                    onClick={() => envoyerAction(tache.id, 'debut')}
                    disabled={isLoading}
                    className="flex items-center gap-1 px-3 py-1.5 bg-green-500 text-white rounded-lg text-sm hover:bg-green-600 disabled:opacity-50 transition"
                  >
                    <Play size={14} />
                    Démarrer
                  </button>
                )}

                {/* PAUSE — visible si en_cours */}
                {en_cours && (
                  <button
                    onClick={() => handlePause(tache)}
                    disabled={isLoading}
                    className="flex items-center gap-1 px-3 py-1.5 bg-yellow-100 text-yellow-700 rounded-lg text-sm hover:bg-yellow-200 disabled:opacity-50 transition"
                  >
                    <Pause size={14} />
                    Pause
                  </button>
                )}

                {/* REPRENDRE — visible si en_pause */}
                {en_pause && (
                  <button
                    onClick={() => handleReprendre(tache)}
                    disabled={isLoading}
                    className="flex items-center gap-1 px-3 py-1.5 bg-green-100 text-green-700 rounded-lg text-sm hover:bg-green-200 disabled:opacity-50 transition"
                  >
                    <RotateCcw size={14} />
                    Reprendre
                  </button>
                )}

                {/* TERMINER — visible si en_cours ou en_pause */}
                {(en_cours || en_pause) && (
                  <button
                    onClick={() => handleTerminer(tache)}
                    disabled={isLoading}
                    className="flex items-center gap-1 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-sm hover:bg-blue-200 disabled:opacity-50 transition"
                  >
                    <Square size={14} />
                    Terminer
                  </button>
                )}

                {isLoading && (
                  <span className="text-xs text-gray-400 self-center animate-pulse">
                    Traitement...
                  </span>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Modal terminer */}
      {showTerminerModal && tacheATerminer && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="font-semibold text-gray-800 mb-4">
              Terminer la tâche — {tacheATerminer.numero_ordre}
            </h3>

            <div className="space-y-3 mb-5">
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Poids bijou fini (g)
                </label>
                <input
                  type="number"
                  step="0.001"
                  value={formTerminer.poids_bijou_fini}
                  onChange={(e) => setFormTerminer({ ...formTerminer, poids_bijou_fini: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-300 outline-none"
                  placeholder="0.000"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Or restant à rendre (g)
                </label>
                <input
                  type="number"
                  step="0.001"
                  value={formTerminer.or_restant}
                  onChange={(e) => setFormTerminer({ ...formTerminer, or_restant: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-300 outline-none"
                  placeholder="0.000"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">Notes</label>
                <textarea
                  value={formTerminer.notes}
                  onChange={(e) => setFormTerminer({ ...formTerminer, notes: e.target.value })}
                  rows={2}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-300 outline-none resize-none"
                  placeholder="Observations..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setShowTerminerModal(false); setTacheATerminer(null); }}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                Annuler
              </button>
              <button
                onClick={confirmerTerminer}
                disabled={actionLoading !== null}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 transition"
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EspaceOuvrier;
